import React, { useEffect, useRef, useState } from 'react';
import { Send, Download, Trash2, Database, Code2, ShieldCheck } from 'lucide-react';

const STORAGE_KEY_PREFIX = 'mi-chat-session';
const EXAMPLE_PROMPTS = [
    { label: 'Dataset freshness', text: 'When was indie gems last generated?' },
    { label: 'Config status', text: 'What config version is active for competitive analysis?' },
    { label: 'Code path', text: 'Where is the Google Sheet import route implemented?' },
    { label: 'Pipeline logic', text: 'How does competitive analysis get generated?' },
];

const ChatAssistant = () => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [storageKey, setStorageKey] = useState(null);
    const [userLabel, setUserLabel] = useState('current browser');
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        const loadHistory = async () => {
            try {
                const response = await fetch('/auth/status');
                const data = await response.json();
                const scopedKey = data?.authenticated && data?.user?.id
                    ? `${STORAGE_KEY_PREFIX}:${data.user.id}`
                    : `${STORAGE_KEY_PREFIX}:browser`;

                if (cancelled) return;

                setStorageKey(scopedKey);
                setUserLabel(data?.authenticated && data?.user?.email ? data.user.email : 'current browser');

                const stored = localStorage.getItem(scopedKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (Array.isArray(parsed)) {
                        setMessages(parsed);
                    }
                }
            } catch (err) {
                console.warn('[ChatAssistant] Failed to load stored messages', err);
                if (cancelled) return;

                const fallbackKey = `${STORAGE_KEY_PREFIX}:browser`;
                setStorageKey(fallbackKey);
                const stored = localStorage.getItem(fallbackKey);
                if (stored) {
                    const parsed = JSON.parse(stored);
                    if (Array.isArray(parsed)) {
                        setMessages(parsed);
                    }
                }
            }
        };

        loadHistory();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (!storageKey) return;
        try {
            localStorage.setItem(storageKey, JSON.stringify(messages));
        } catch (err) {
            console.warn('[ChatAssistant] Failed to persist messages', err);
        }
    }, [messages, storageKey]);

    useEffect(() => {
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = async (evt) => {
        evt?.preventDefault();
        if (!input.trim()) return;

        const userMsg = { role: 'user', content: input.trim(), ts: Date.now() };
        setMessages(prev => [...prev, userMsg]);
        setInput('');
        setSending(true);
        setError(null);

        try {
            const history = messages.slice(-6).map(({ role, content }) => ({ role, content }));
            const res = await fetch('/analytics/v2/chat/message', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ message: userMsg.content, history })
            });

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const json = await res.json();
            console.log('[ChatAssistant] Response:', json);

            const assistantMsg = {
                role: 'assistant',
                content: json.error || json.reply || 'No reply received.',
                ts: Date.now(),
                scope: json.scope || null,
            };

            setMessages(prev => [...prev, assistantMsg]);
        } catch (err) {
            console.error('[ChatAssistant] Error:', err);
            setError('Failed to send message. Try again.');
            setMessages(prev => [
                ...prev,
                { role: 'assistant', content: 'Error sending message: ' + err.message, ts: Date.now() }
            ]);
        } finally {
            setSending(false);
        }
    };

    const handleDownload = () => {
        const lines = messages.map(m => {
            const time = new Date(m.ts || Date.now()).toISOString();
            const speaker = m.role === 'user' ? 'You' : 'Assistant';
            return `[${time}] ${speaker}: ${m.content}`;
        }).join('\n\n');
        const blob = new Blob([lines], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `chat-log-${new Date().toISOString().slice(0,10)}.txt`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const handleClear = () => {
        setMessages([]);
        setError(null);
    };

    return (
        <div className="h-full flex flex-col overflow-hidden rounded-2xl border border-sky-200 bg-gradient-to-br from-slate-50 via-sky-50 to-cyan-50 shadow-lg">
            <div className="bg-gradient-to-r from-slate-900 via-sky-900 to-cyan-800 px-4 py-4 text-white">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold">
                            <ShieldCheck className="h-4 w-4 text-cyan-200" />
                            <span>Scoped AI Analyst</span>
                        </div>
                        <div className="mt-1 text-xs text-sky-100">
                            Answers only dataset and codebase questions. Everything else gets rejected.
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-sky-50">
                                <Database className="h-3.5 w-3.5" />
                                Dataset queries
                            </span>
                            <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 text-[11px] text-sky-50">
                                <Code2 className="h-3.5 w-3.5" />
                                Codebase queries
                            </span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            className="rounded-lg p-2 text-white transition-all hover:bg-white/10 hover:text-sky-100"
                            title="Save conversation"
                            onClick={handleDownload}
                        >
                            <Download className="h-4 w-4" />
                        </button>
                        <button
                            className="rounded-lg p-2 text-white transition-all hover:bg-white/10 hover:text-red-200"
                            title="Clear conversation"
                            onClick={handleClear}
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="border-b border-sky-100 bg-white/90 px-4 py-3">
                <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                    Example Prompts
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                    {EXAMPLE_PROMPTS.map((example) => (
                        <button
                            key={example.text}
                            type="button"
                            onClick={() => setInput(example.text)}
                            className="rounded-full border border-sky-200 bg-sky-50 px-3 py-1.5 text-[11px] text-slate-700 transition hover:border-sky-300 hover:bg-sky-100"
                        >
                            {example.label}
                        </button>
                    ))}
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                    History is stored locally for <span className="font-medium text-slate-700">{userLabel}</span>.
                </div>
            </div>

            <div ref={containerRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
                {messages.length === 0 && (
                    <div className="rounded-2xl border border-dashed border-sky-200 bg-white/70 px-5 py-8 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-sky-100 text-sky-700">
                            <ShieldCheck className="h-6 w-6" />
                        </div>
                        <div className="text-sm font-semibold text-slate-800">Ask one scoped question at a time</div>
                        <div className="mx-auto mt-2 max-w-xs text-xs leading-5 text-slate-500">
                            Good fits: dataset freshness, config versions, ranking logic, routes, services, and implementation details.
                        </div>
                    </div>
                )}
                {messages.map((m, idx) => (
                    <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[88%] ${m.role === 'user' ? 'order-2' : 'order-1'}`}>
                            <div
                                className={`whitespace-pre-wrap rounded-2xl p-3 text-sm shadow-sm ${
                                    m.role === 'user'
                                        ? 'rounded-br-sm bg-gradient-to-br from-sky-600 to-cyan-600 text-white'
                                        : 'rounded-bl-sm border border-sky-100 bg-white text-slate-800'
                                }`}
                            >
                                {m.content}
                            </div>
                            <div className={`mt-1 flex items-center gap-2 px-2 text-xs text-slate-500 ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                <span>{m.role === 'user' ? 'You' : 'Assistant'}</span>
                                {m.scope && m.role !== 'user' && (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600">
                                        {m.scope}
                                    </span>
                                )}
                                <span>{new Date(m.ts || Date.now()).toLocaleTimeString()}</span>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {error && (
                <div className="flex items-center gap-2 border-t border-red-200 bg-red-50 px-4 py-2 text-xs text-red-700">
                    <span className="text-red-500">⚠️</span>
                    {error}
                </div>
            )}

            <form onSubmit={handleSend} className="border-t border-sky-100 bg-white/90 p-4 backdrop-blur-sm">
                <div className="mb-2 flex items-center justify-between gap-3">
                    <div className="text-[11px] font-medium uppercase tracking-[0.18em] text-slate-500">
                        Ask About Datasets Or Code
                    </div>
                    <div className="text-[11px] text-slate-400">
                        Enter to send, Shift+Enter for a new line
                    </div>
                </div>
                <div className="flex items-end gap-2">
                    <textarea
                        className="flex-1 resize-none rounded-2xl border border-sky-200 bg-white px-4 py-3 text-sm text-slate-800 shadow-sm transition focus:border-sky-400 focus:ring-2 focus:ring-sky-200"
                        rows={3}
                        placeholder="Ask about datasets or this codebase..."
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSend(e);
                            }
                        }}
                        disabled={sending}
                    />
                    <button
                        type="submit"
                        disabled={sending || !input.trim()}
                        className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-sky-600 to-cyan-600 px-4 py-3 text-white shadow-sm transition hover:from-sky-700 hover:to-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        {sending ? (
                            <>
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                                <span className="text-sm font-medium">Thinking...</span>
                            </>
                        ) : (
                            <>
                                <Send className="h-4 w-4" />
                                <span className="text-sm font-medium">Send</span>
                            </>
                        )}
                    </button>
                </div>
                <div className="mt-2 text-[11px] text-slate-500">
                    Out-of-scope prompts are refused by design.
                </div>
            </form>
        </div>
    );
};

export default ChatAssistant;
